import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  FolderOpen,
  FileText,
  DollarSign,
  TrendingUp,
  Cpu,
  Loader2,
  AlertTriangle,
  Save,
  Link2,
  Trash2,
  Inbox,
  Mail,
  Building2,
  Download,
} from "lucide-react";

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Metric({ icon: Icon, label, value, sub, testid, tone }) {
  return (
    <div
      className={`rounded-sm border bg-card p-5 ${tone === "alert" ? "border-destructive/50" : "border-border"}`}
      data-testid={testid}
    >
      <div className={`flex items-center gap-2 ${tone === "alert" ? "text-destructive" : "text-primary"}`}>
        <Icon size={17} />
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
      </div>
      <div className="mt-3 font-serif text-3xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-sm text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  const [leads, setLeads] = useState(null);
  const [leadFilter, setLeadFilter] = useState("all");

  const loadLinks = () =>
    api.get("/admin/share-links").then((r) => setLinks(r.data)).catch(() => {});

  const loadLeads = () =>
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads({ leads: [], counts: {}, total: 0 }));

  const setLeadStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      await loadLeads();
    } catch {
      toast.error("Could not update lead");
    }
  };

  const load = () =>
    api
      .get("/admin/analytics")
      .then((r) => {
        setData(r.data);
        setThreshold(String(r.data.settings.cost_alert_threshold_usd));
        setRate(String(r.data.settings.inr_per_usd));
      })
      .catch(() => setError(true));

  useEffect(() => {
    if (user && user.role !== "admin") return;
    load();
    loadLinks();
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const revoke = async (token) => {
    try {
      await api.post(`/admin/share-links/${token}/revoke`);
      toast.success("Link revoked");
      await loadLinks();
    } catch {
      toast.error("Could not revoke link");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        cost_alert_threshold_usd: parseFloat(threshold),
        inr_per_usd: parseFloat(rate),
      });
      toast.success("Settings saved");
      await load();
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const thr = parseFloat(threshold) || 0;
  const rateNum = parseFloat(rate) || 0;
  const unifiedUsd =
    data && rateNum > 0 ? data.revenue.usd + data.revenue.inr / rateNum : data ? data.revenue.usd : 0;
  const unifiedMargin = data ? unifiedUsd - data.ai.cost_usd : 0;

  return (
    <div className="min-h-screen bg-background">
      <TopBar>
        <div className="hidden px-2 font-serif text-lg font-medium sm:block">Admin console</div>
      </TopBar>
      <main className="mx-auto max-w-6xl px-6 py-8" data-testid="admin-page">
        <div className="mb-8 flex items-center gap-3">
          <BarChart3 size={22} className="text-primary" />
          <div>
            <h1 className="font-serif text-2xl font-medium tracking-tight">Analytics &amp; margins</h1>
            <p className="text-sm text-muted-foreground">AI cost vs. revenue and per-project generation stats.</p>
          </div>
        </div>

        {error ? (
          <div className="rounded-sm border border-destructive/40 bg-destructive/5 p-8 text-center text-destructive">
            Could not load analytics.
          </div>
        ) : !data ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="mr-2 animate-spin" size={18} /> Loading analytics…
          </div>
        ) : (
          <div className="space-y-8">
            {/* Controls: cost threshold + currency rate */}
            <div className="grid grid-cols-1 gap-4 rounded-sm border border-border bg-card p-6 lg:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Cost alert threshold (USD / project)
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    className="rounded-sm"
                    data-testid="cost-threshold-input"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Projects whose AI cost exceeds this are flagged below.
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Exchange rate (INR per 1 USD)
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="1"
                    step="0.5"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className="rounded-sm"
                    data-testid="currency-rate-input"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Used to convert INR revenue into the unified USD total.</p>
              </div>
              <div className="lg:col-span-2">
                <Button onClick={saveSettings} disabled={saving} className="rounded-sm" data-testid="save-settings-button">
                  {saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Save size={15} className="mr-2" />}
                  Save settings
                </Button>
              </div>
            </div>

            {/* Financial row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric
                icon={DollarSign}
                label="Revenue (USD)"
                value={`$${data.revenue.usd.toLocaleString()}`}
                sub={`${data.revenue.paypal_payments} PayPal · ${data.revenue.subscriptions} subs`}
                testid="metric-revenue-usd"
              />
              <Metric
                icon={DollarSign}
                label="Revenue (INR)"
                value={`₹${data.revenue.inr.toLocaleString()}`}
                sub={`${data.revenue.upi_payments} UPI payments`}
                testid="metric-revenue-inr"
              />
              <Metric
                icon={TrendingUp}
                label="Unified revenue"
                value={`$${unifiedUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                sub={`USD + INR @ ₹${rateNum || "—"}/$`}
                testid="metric-unified-revenue"
              />
              <Metric
                icon={TrendingUp}
                label="Unified margin"
                value={`$${unifiedMargin.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                sub={`after $${data.ai.cost_usd} AI cost`}
                testid="metric-unified-margin"
              />
            </div>

            {/* Volume row */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <Metric icon={Users} label="Users" value={data.totals.users} testid="metric-users" />
              <Metric icon={FolderOpen} label="Projects" value={data.totals.projects} testid="metric-projects" />
              <Metric
                icon={FileText}
                label="Docs made"
                value={data.totals.documents_generated}
                sub={`of ${data.totals.documents_total}`}
                testid="metric-docs"
              />
              <Metric icon={Cpu} label="AI cost" value={`$${data.ai.cost_usd.toLocaleString()}`} sub={`$${data.ai.avg_cost_per_doc}/doc`} testid="metric-ai-cost" />
              <Metric
                icon={DollarSign}
                label="Credits sold"
                value={data.revenue.credits_sold.toLocaleString()}
                testid="metric-credits-sold"
              />
              <Metric
                icon={AlertTriangle}
                label="Cost alerts"
                value={data.cost_alerts}
                sub={data.cost_alerts > 0 ? `over $${thr}` : "all under budget"}
                tone={data.cost_alerts > 0 ? "alert" : undefined}
                testid="metric-cost-alerts"
              />
            </div>

            {/* Per-project table */}
            <div className="rounded-sm border border-border bg-card">
              <div className="border-b border-border px-6 py-4">
                <h3 className="font-serif text-lg font-semibold">Per-project generation</h3>
                <p className="text-sm text-muted-foreground">{data.margin.note}</p>
              </div>
              {data.projects.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">No generation activity yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="project-stats-table">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-6 py-3 font-medium">Project</th>
                        <th className="px-6 py-3 font-medium">Owner</th>
                        <th className="px-6 py-3 text-right font-medium">Docs</th>
                        <th className="px-6 py-3 text-right font-medium">Words</th>
                        <th className="px-6 py-3 text-right font-medium">AI cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projects.map((p) => {
                        const over = p.ai_cost_usd > thr;
                        return (
                          <tr
                            key={p.project_id}
                            className={`border-b border-border/60 last:border-0 ${over ? "bg-destructive/5" : ""}`}
                            data-testid={`project-row-${p.project_id}`}
                          >
                            <td className="px-6 py-3 font-medium">
                              <div className="flex items-center gap-2">
                                {over && <AlertTriangle size={13} className="text-destructive" data-testid={`alert-flag-${p.project_id}`} />}
                                {p.title}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-muted-foreground">{p.owner_email || "—"}</td>
                            <td className="px-6 py-3 text-right">{p.docs_generated}</td>
                            <td className="px-6 py-3 text-right text-muted-foreground">{p.words.toLocaleString()}</td>
                            <td className={`px-6 py-3 text-right ${over ? "font-semibold text-destructive" : ""}`}>
                              ${p.ai_cost_usd}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Sales leads inbox */}
            <div className="rounded-sm border border-border bg-card" data-testid="leads-panel">
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-4">
                <Inbox size={17} className="text-[hsl(var(--teal))]" />
                <h3 className="font-serif text-lg font-semibold">Sales leads</h3>
                {leads && (
                  <span className="text-sm text-muted-foreground">
                    ({leads.counts?.new || 0} new · {leads.total || 0} total)
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {leads && leads.total > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-sm font-mono text-[11px] uppercase tracking-wider"
                      onClick={() => downloadFile("/leads/export.csv", `espresso-leads-${new Date().toISOString().slice(0, 10)}.csv`)}
                      data-testid="leads-export-csv"
                    >
                      <Download size={13} className="mr-1.5" /> Export CSV
                    </Button>
                  )}
                  <div className="flex items-center gap-1">
                    {["all", "new", "contacted", "closed"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setLeadFilter(f)}
                        data-testid={`lead-filter-${f}`}
                        className={`rounded-sm px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                          leadFilter === f
                            ? "bg-[hsl(var(--teal))] text-[hsl(var(--teal-foreground))]"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {!leads ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="mr-2 animate-spin" size={16} /> Loading leads…
                </div>
              ) : (leads.leads || []).filter((l) => leadFilter === "all" || l.status === leadFilter).length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">No {leadFilter === "all" ? "" : leadFilter} leads yet.</div>
              ) : (
                <div className="divide-y divide-border/60">
                  {(leads.leads || [])
                    .filter((l) => leadFilter === "all" || l.status === leadFilter)
                    .map((l) => (
                      <div key={l.id} className="px-6 py-4" data-testid={`lead-row-${l.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">{l.name}</span>
                              <span
                                className={`rounded-sm px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${
                                  l.status === "new"
                                    ? "bg-[hsl(var(--matcha)/0.18)] text-[hsl(var(--matcha))]"
                                    : l.status === "contacted"
                                    ? "bg-[hsl(var(--teal)/0.15)] text-[hsl(var(--teal))]"
                                    : "bg-secondary text-muted-foreground"
                                }`}
                              >
                                {l.status}
                              </span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                              <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`lead-email-${l.id}`}>
                                <Mail size={13} /> {l.email}
                              </a>
                              {l.organization && (
                                <span className="inline-flex items-center gap-1">
                                  <Building2 size={13} /> {l.organization}
                                </span>
                              )}
                              <span className="font-mono text-[11px]">{l.source}</span>
                              {l.created_at && (
                                <span className="font-mono text-[11px]">{new Date(l.created_at).toLocaleDateString()}</span>
                              )}
                            </div>
                            {l.message && <p className="mt-2 max-w-2xl text-sm text-foreground/80">{l.message}</p>}
                          </div>
                          <div className="flex shrink-0 gap-1">
                            {["new", "contacted", "closed"].map((s) => (
                              <Button
                                key={s}
                                size="sm"
                                variant={l.status === s ? "default" : "outline"}
                                className="h-7 rounded-sm px-2 font-mono text-[10px] uppercase tracking-wider"
                                onClick={() => setLeadStatus(l.id, s)}
                                data-testid={`lead-set-${s}-${l.id}`}
                                disabled={l.status === s}
                              >
                                {s}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Shareable export links */}
            <div className="rounded-sm border border-border bg-card" data-testid="share-links-panel">
              <div className="flex items-center gap-2 border-b border-border px-6 py-4">
                <Link2 size={17} className="text-primary" />
                <h3 className="font-serif text-lg font-semibold">Shareable export links</h3>
                <span className="text-sm text-muted-foreground">({links.length} active)</span>
              </div>
              {links.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">No active share links.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="share-links-table">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-6 py-3 font-medium">File</th>
                        <th className="px-6 py-3 font-medium">Project</th>
                        <th className="px-6 py-3 font-medium">Owner</th>
                        <th className="px-6 py-3 text-right font-medium">Size</th>
                        <th className="px-6 py-3 font-medium">Expires</th>
                        <th className="px-6 py-3 text-right font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {links.map((l) => (
                        <tr key={l.token} className="border-b border-border/60 last:border-0" data-testid={`share-link-row-${l.token}`}>
                          <td className="px-6 py-3">
                            <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                              {l.format}
                            </span>{" "}
                            {l.filename}
                          </td>
                          <td className="px-6 py-3 text-muted-foreground">{l.project_title}</td>
                          <td className="px-6 py-3 text-muted-foreground">{l.owner_email || "—"}</td>
                          <td className="px-6 py-3 text-right text-muted-foreground">{fmtBytes(l.size)}</td>
                          <td className={`px-6 py-3 ${l.expired ? "text-destructive" : "text-muted-foreground"}`}>
                            {l.expired ? "Expired" : new Date(l.expires_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-sm text-destructive"
                              onClick={() => revoke(l.token)}
                              data-testid={`revoke-link-${l.token}`}
                            >
                              <Trash2 size={14} className="mr-1" /> Revoke
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
